import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Grid,
  Card,
  Stack,
  Skeleton,
  CardHeader,
  CardContent,
  Typography,
  InputAdornment,
  Button,
  Box,
  Divider,
  IconButton,
  CardActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
// components
import {
  FormProvider,
  RHFTextField,
  RHFMainRoleSelect,
  RHFSubRoleSelect,
  RHFFileOrURL,
  RHFCategorySelect,
} from '../../components/hook-form';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import {
  AddCareerDialog,
  ModifyCareerDialog,
  HideCareerDialog,
  ProfilePreviewDialog,
  AIProfileConfirmDialog,
  AIProfileDeleteDialog,
  AICareerDialog,
  CareerStatusCard,
  AdditionalSkillCard,
} from './';
import {
  MatchingOnDialog,
} from '../settings';
import Label from '../../components/Label';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import axios from '../../utils/axios';
import {default as axiosNormal} from 'axios';
import { fKrDateYYYYMMKST } from '../../utils/formatTime';

const profilePlaceholder = `본인을 소개해주세요 (20자 이상)
- 본업 관련 프로젝트 상세
- 창업 및 사이드 프로젝트 경험
- 리더십 경험
- 익숙한 기술/도메인`;


export default function MatchingProfile() {
  const { user, application, getApplication } = useAuth();
  const { data: profileEmployment, isLoading: isProfileEmploymentLoading } = useSWR(user ? `/api/crews/profile/employment/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { data: profileSkill, isLoading: isProfileSkillLoading } = useSWR(user ? `/api/crews/profile/skill/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');
  const { enqueueSnackbar } = useSnackbar();

  const emptyEmployment = !profileEmployment?.employment_type && !profileEmployment?.work_place && !profileEmployment?.interest_in_early_startup && !profileEmployment?.interest_in_changing_job;
  const emptySkill = !profileSkill?.full_stack_experience && !profileSkill?.ai_usage && !profileSkill?.content_experience && !profileSkill?.english_proficiency;
  const suggestMoreInfo = emptyEmployment || emptySkill;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [roleOption, setRoleOption] = useState([]);
  const [skillOption, setSkillOption] = useState([]);
  const [needPortfolio, setNeedPortfolio] = useState(false);
  const [resumeType, setResumeType] = useState('링크');
  const [portfolioType, setPortfolioType] = useState('파일');
  const [showSubRoles, setShowSubRoles] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  // career dialog
  const [isOpenAddCareerDialog, setIsOpenAddCareerDialog] = useState(false);
  const [isOpenModifyCareerDialog, setIsOpenModifyCareerDialog] = useState(false);
  const [isOpenHideCareerDialog, setIsOpenHideCareerDialog] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState({});
  const [selectedCareerIndex, setSelectedCareerIndex] = useState(null);
  const [hasWorkingCareer, setHasWorkingCareer] = useState(false);

  // turn on matching dialog
  const [isOpenTurnOnMatching, setIsOpenTurnOnMatching] = useState(false);

  // ai
  const [isOpenAIProfileConfirm, setIsOpenAIProfileConfirm] = useState(false);
  const [isOpenAIProfileDelete, setIsOpenAIProfileDelete] = useState(false);
  const [selectedAICareer, setSelectedAICareer] = useState({});
  const [selectedAICareerIndex, setSelectedAICareerIndex] = useState(null);
  const [isOpenAICareer, setIsOpenAICareer] = useState(false);

  // preview
  const [isOpenProfilePreview, setIsOpenProfilePreview] = useState(false);

  const MatchingProfileSchema = Yup.object().shape({
    roles: Yup.array().length(1, '포지션을 선택하세요'),
    careers: Yup.array().required('경력을 입력해주세요').min(1, '경력을 입력해주세요'),
    categories: Yup.array().min(1, '카테고리를 1개 이상 선택하세요').max(3, '카테고리를 3개 이하로 선택하세요'),
    years: Yup.number().required('숫자를 입력해주세요').typeError('숫자를 입력해주세요').positive('양수를 입력해주세요'),
    FOUresumeType: Yup.string(),
    FOUresumeURL: Yup.string(),
    FOUresumeFile: Yup.mixed(),
    FOUportfolioType: Yup.string(),
    FOUportfolioURL: Yup.string(),
    FOUportfolioFile: Yup.mixed(),
    profile: Yup.string().required('익명 프로필을 입력해주세요').min(20, '20자 이상 입력해주세요'),
  })

  const defaultValues = {
    roles: application?.main_role ? [application.main_role] : [],
    subRoles: application?.matching_roles || [],
    careers: application?.careers || [],
    categories: application?.categories || [],
    years: application?.years_of_experience || 0,
    FOUresumeType: application?.urls?.find(f => f.name === '이력서 링크') ? 'FOUresumeURL' : 'FOUresumeFile',
    FOUresumeURL: '',
    FOUresumeFile: {id: null, name: '', file: '', file_type: 'resume'},
    FOUportfolioType: application?.urls?.find(f => f.name === '포트폴리오 링크') ? 'FOUportfolioURL' : 'FOUportfolioFile',
    FOUportfolioURL: '',
    FOUportfolioFile: {id: null, name: '', file: '', file_type: 'portfolio'},
    profile: application?.profile || '',
  };

  const methods = useForm({
    resolver: yupResolver(MatchingProfileSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    getValues,
    setValue,
    setError,
    trigger,
    formState: { errors },
  } = methods;

  const roleWatch = watch('roles');
  const subRoleWatch = watch('subRoles');
  const categoryWatch = watch('categories');

  const watchValues = async (value, name, type) => {
    if (name === 'careers') {
      setHasWorkingCareer(value.careers.some(career => career.end_date === null));
    }
    if (type === 'change' && name === 'roles' && value?.roles?.length === 1) {
      const newSubRoles = value.subRoles;
      const roleIndex = newSubRoles.findIndex((r) => r.role === value.roles[0].role);
      if (roleIndex > -1) {
        newSubRoles.splice(roleIndex, 1);
        setValue('subRoles', newSubRoles);
      }
      trigger('roles');
    } else if (type === 'change' && name === 'categories') {
      trigger('categories');
    }
  };

  const openSubPosition = async () => {
    setShowSubRoles(true);
  };

  const askMatchingOn = async () => {
    if(!user?.crew?.is_matching_allowed) {
      const availableHour = user?.crew?.limit_working_hours_per_week - user?.crew?.current_working_hours_per_week - user?.crew?.matching_working_hours_per_week;
      if (availableHour >= 10) {
        setIsOpenTurnOnMatching(true);
      }
    }
  }

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true);
        const profileOptions = await axios.get('api/crews/profile-options/');
        setRoleOption(profileOptions.data.matching_roles);
        setSkillOption(profileOptions.data.skills);
        setCategoryOptions(profileOptions.data.categories);

        setNeedPortfolio(user.crew.position === "designer" || user.crew.position === "marketer")

        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    }
    if (user && roleOption.length === 0) {
      initialize();
    }

    const resumeFile = application?.files?.find(f => f.file_type === 'resume');
    const resumeURL = application?.urls?.find(f => f.name === '이력서 링크');
    const portfolioURL = application?.urls?.find(f => f.name === '포트폴리오 링크');
    const portfolioFile = application?.files?.find(f => f.file_type === 'portfolio');
    setResumeType(resumeFile ? '파일' : 'URL');
    setPortfolioType(portfolioURL ? 'URL' : '파일');

    setError("FOUresumeFile", { type: "custom", message: 'error' });
    setError("FOUresumeURL", { type: "custom", message: 'error' });

    reset({
      roles: application?.main_role ? [application.main_role] : [],
      subRoles: application?.matching_roles || [],
      categories: application?.categories || [],
      careers: application?.careers || [],
      years: application?.years_of_experience || 0,
      FOUresumeType: resumeFile ? 'FOUresumeFile' : 'FOUresumeURL',
      FOUresumeURL: resumeURL?.url || '',
      FOUresumeFile: {id: resumeFile?.id || null, name: resumeFile?.file_name || '', file: resumeFile?.file || '', file_type: 'resume'},
      FOUportfolioType: portfolioURL ? 'FOUportfolioURL' : 'FOUportfolioFile',
      FOUportfolioURL: portfolioURL?.url || '',
      FOUportfolioFile: {id: portfolioFile?.id || null, name: portfolioFile?.file_name || '', file: portfolioFile?.file || '', file_type: 'portfolio'},
      profile: application?.profile || '',
    });

    trigger(["roles", "categories", "careers"]);
    if (application?.matching_roles?.length > 0) setShowSubRoles(true);
    setHasWorkingCareer(application?.careers?.some(career => career.end_date === null));

    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();

  }, [application, watch]);

  const fileUploadBeforeSubmit = async (files) => {
    const fileNameList = [];
    for (let fileDict of files) {
      const file = fileDict.file;
      if (file.id && file.name) {
        fileNameList.push({id: file.id, name: file.name, file_type: fileDict.file_type});
      } else if (file.name === '' || file.name === null) {
        continue;
      } else {
        const fileURLResponse = await axios.post(
          `/api/crews/file-url/`,
          {filename: file.name}
        );
        const {filename, url} = fileURLResponse.data;

        await axiosNormal.put(
          url,
          file,
          {
            headers: {
              'Content-Type': file.type
            }
          }
        );
        fileNameList.push({name: filename, file_type: fileDict.file_type});
      }
    }
    return fileNameList;
  };

  const onSubmit = async (formData) => {
    try {
      // type 에 따라서 url 을 쓸건지 file 을 쓸건지 정하기
      const filteredURLs = [];
      const filteredFiles = [];
      if (needPortfolio && formData.FOUportfolioType === "FOUportfolioURL") {
        if (formData.FOUportfolioURL !== "") {
          filteredURLs.push({
            name: '포트폴리오 링크',
            url: formData.FOUportfolioURL
          })
        }
      }
      if (needPortfolio && formData.FOUportfolioType === "FOUportfolioFile") {
        if (formData.FOUportfolioFile.file !== "") {
          filteredFiles.push({
            file: formData.FOUportfolioFile,
            file_type: 'portfolio',
          })
        }
      }
      if (formData.FOUresumeType === "FOUresumeURL") {
        if (formData.FOUresumeURL === "") {
          setError("FOUresumeURL", { type: "custom", message: '링크를 입력해주세요' });
          return
        }
        filteredURLs.push({
          name: '이력서 링크',
          url: formData.FOUresumeURL
        })
      }
      if (formData.FOUresumeType === "FOUresumeFile") {
        if (formData.FOUresumeFile.file === "") {
          setError("FOUresumeFile", { type: "custom", message: '파일을 선택해주세요' });
          return
        }
        filteredFiles.push({
          file: formData.FOUresumeFile,
          file_type: 'resume'
        })
      }
      setIsSubmitting(true);
      const fileUploadedData = await fileUploadBeforeSubmit(filteredFiles);

      const patchData = {
        years_of_experience: formData.years,
        roles: formData.roles,
        sub_roles: formData.subRoles,
        careers: formData.careers,
        categories: formData.categories,
        urls: filteredURLs,
        files: fileUploadedData,
        profile: formData.profile,
      }
      await axios.patch(
        `/api/crews/profile/`,
        patchData
      );

      enqueueSnackbar('저장 하였습니다');
      setIsSubmitting(false);
      getApplication();
      askMatchingOn();
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
      enqueueSnackbar('저장에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
    }
  };

  const openCareerDialog = async () => {
    setIsOpenAddCareerDialog(true);
  };

  const closeAddCareerDialog = async () => {
    setIsOpenAddCareerDialog(false);
  };

  const openModifyCareerDialog = async (selectedCareer, selectedIndex) => {
    setSelectedCareer(selectedCareer);
    setSelectedCareerIndex(selectedIndex);
    setIsOpenModifyCareerDialog(true);
  };

  const closeModifyCareerDialog = async () => {
    setSelectedCareer({});
    setSelectedCareerIndex(null);
    setIsOpenModifyCareerDialog(false);
  };

  const openHideCareerDialog = async (e, selectedCareer, selectedIndex) => {
    e.stopPropagation();
    setSelectedCareer(selectedCareer);
    setSelectedCareerIndex(selectedIndex);
    setIsOpenHideCareerDialog(true);
  };

  const closeHideCareerDialog = async () => {
    setSelectedCareerIndex(null);
    setIsOpenHideCareerDialog(false);
  };

  const openAICareerDialog = async (selectedCareer, selectedIndex) => {
    setSelectedAICareer(selectedCareer);
    setSelectedAICareerIndex(selectedIndex);
    setIsOpenAICareer(true);
  };

  const closeAICareerDialog = async () => {
    setSelectedAICareer({});
    setSelectedAICareerIndex(null);
    setIsOpenAICareer(false);
  };

  const addCareer = async (career) => {
    const currentCareers = getValues().careers;
    currentCareers.push(career);
    setValue('careers', currentCareers);
  };

  const modifyCareer = async (career) => {
    const currentCareers = getValues().careers;
    const modifyCareer = currentCareers[career.index];
    currentCareers[career.index] = {...modifyCareer, ...career};
    setValue('careers', currentCareers);
  };

  const removeCareer = async (career) => {
    const currentCareers = getValues().careers;
    currentCareers.splice(career.index, 1);
    setValue('careers', currentCareers);
  };

  const hideCareer = async (career) => {
    const currentCareers = getValues().careers;
    const hideTarget = currentCareers[career.index];
    currentCareers[career.index] = {
      ...hideTarget,
      alias_name: career.alias_name,
      is_hidden_company_name: true,
      is_direct_input_company_name: career.is_direct_input_company_name,
      selected_size: career.selected_size,
      selected_domain: career.selected_domain,
    };
    setValue('careers', currentCareers);
  };

  const unHideCareer = async (career) => {
    const currentCareers = getValues().careers;
    const unHideTarget = currentCareers[career.index];
    currentCareers[career.index] = {
      ...unHideTarget,
      alias_name: null,
      is_hidden_company_name: false,
      is_direct_input_company_name: career.is_direct_input_company_name,
      selected_size: career.selected_size,
      selected_domain: career.selected_domain,
    };
    setValue('careers', currentCareers);
  };

  const careerDisplayName = (career) => {
    return career.is_hidden_company_name ? career.alias_name : career.company_name;
  };

  const careerDisplayPeriod = (career) => {
    return career.is_hidden_career_period ? '재직기간 비공개' : `${fKrDateYYYYMMKST(career.start_date)} ~ ${career.end_date === null ? '현재' : fKrDateYYYYMMKST(career.end_date)}`;
  };

  const submitError = async (errors) => {
    if (errors.roles && errors.categories) {
      enqueueSnackbar('포지션과 경력 카테고리를 선택하세요', { variant: 'error' })
    } else if (errors.roles) {
      enqueueSnackbar(errors.roles.message, { variant: 'error' })
    } else if (errors.categories) {
      enqueueSnackbar(errors.categories.message, { variant: 'error' })
    }
  }

  const openFile = async (item) => {
    const response = await axios.post(`/api/crews/profile/file/${item.id}/url/`);
    window.open(response.data.url);
  };

  const openProfilePreview = async () => {
    setIsOpenProfilePreview(true);
  };

  const scrollToCareer = async () => {
    const careerRef = document.getElementById('careerInput');
    careerRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <Grid item xs={12}>
      {(!application || isLoading) && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={112} />
                <Skeleton variant="text" height={56} />
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Skeleton variant="text" height={112} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {application && !isLoading && (
        <FormProvider methods={methods}>
          <Stack spacing={3}>
            {suggestMoreInfo ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.200",
                }}
              >
                <Stack direction="row" spacing={1}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 2,
                      backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SvgIconStyle src={`/icons/icon-lightning-mono.svg`} sx={{ width: 15, height: 15, color: "text.secondary" }} />
                  </Box>
                  <Stack sx={{ width: 1 }} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center"}} spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Stack>
                        <Typography variant="body2">
                          <b>커리어 상태</b>와 <b>추가 스킬</b>을 입력해주세요!
                        </Typography>
                        <Typography variant="body2">
                          고용 형태, 영어 가능 여부 등 프로필을 업데이트 해주시면 딱 맞는 제안을 받을 수 있어요.
                        </Typography>
                      </Stack>
                    </Stack>
                    <Button
                      variant="contained"
                      onClick={scrollToCareer}
                      disableRipple
                    >
                      입력하기
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: "grey.200",
                }}
              >
                <Stack direction={{ xs: "column", sm : "row" }} justifyContent={{ xs: "flex-start", sm: "space-between" }} spacing={{ xs: 1, sm: 2 }} alignItems={{ sm: "center", xs: "flex-start"}}>
                  <Stack direction={"row"} spacing={1} alignItems={smUp ? "center" : "flex-start"}>
                    <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <b>프로필을 개선</b>하고, 매칭을 더 받아보세요!
                    </Typography>
                  </Stack>
                  <Stack sx={{ pl: { xs: 4, sm: 0 } }}>
                    <Button
                      variant="contained"
                      onClick={() => window.open("https://dio.oopy.io/15ea8b46-657c-80b7-a045-c163044b8023", "_blank", "noopener,noreferrer")}
                      disableRipple
                      endIcon={<Iconify icon="eva:external-link-fill" />}
                    >
                      프로필 작성 팁 보기
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}
            <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
              <CardHeader
                title="프로필"
                action={
                  <Button
                    aria-label="preview"
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 2, px: 1.5, py: 0.5 }}
                    endIcon={
                      <Iconify sx={{ mx: 0 }} icon="ic:baseline-chevron-right" />
                    }
                    disableRipple
                    onClick={openProfilePreview}
                  >
                    기업에 이렇게 전달돼요
                  </Button>
                }
              />
              <CardContent>
                <Stack spacing={3}>
                  <Stack>
                  <Typography variant="subtitle2" gutterBottom>
                    가장 잘할 수 있는 포지션
                  </Typography>
                  <RHFMainRoleSelect
                    name="roles"
                    position={user?.crew?.position}
                    placeholder={(roleWatch && roleWatch.length > 0) ? "가장 자신 있어요." : "포지션 1개만 선택해주세요."}
                    options={roleOption}
                  />
                  {roleWatch && roleWatch.length > 0 && !showSubRoles && (
                  <Button
                    sx={{ mt: 0.5, justifyContent: 'start', width: '200px', color: 'text.secondary' }}
                    startIcon={<Iconify icon="ic:round-plus" />}
                    size="small"
                    onClick={openSubPosition}
                  >
                    포지션 추가하기
                  </Button>
                  )}
                  </Stack>

                  {showSubRoles && (
                  <Stack>
                  <Typography variant="subtitle2" gutterBottom>
                    추가 포지션
                  </Typography>
                  <RHFSubRoleSelect
                    name="subRoles"
                    position={user?.crew?.position}
                    placeholder={(subRoleWatch && subRoleWatch.length > 0) ? "더 추가하기" : "일할 수 있는 다른 포지션을 선택해주세요."}
                    options={roleOption}
                    mainRole={getValues()?.roles?.[0]?.role || null}
                  />
                  </Stack>
                  )}

                  <Stack>
                  <Typography variant="subtitle2" gutterBottom>
                    경력 카테고리
                  </Typography>
                  <RHFCategorySelect
                    name="categories"
                    placeholder={(!categoryWatch || categoryWatch.length < 3) ? "최대 3개까지 선택할 수 있습니다." : ""}
                    options={categoryOptions}
                  />
                  </Stack>

                  {/* career */}
                  <Stack>
                    <Typography variant="subtitle2">
                      경력 사항
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }} gutterBottom>
                      직장명 및 재직 기간의 노출을 원치 않는 경력은 <b>가리기</b>를 눌러 수정해주세요.
                    </Typography>
                    <Stack spacing={2}>
                      {getValues().careers?.map((career, index) => (
                        <Box
                          key={index}
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
                            '&:hover': {
                              cursor: 'pointer',
                            },
                          }}
                          onClick={() => openModifyCareerDialog(career, index)}
                        >
                          <Box
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              display: 'flex',
                            }}
                          >
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 2, overflow: "hidden", width: 1 }}>
                            <Typography sx={{ width: '100%', maxWidth: mdUp ? "100%" : smUp ? "300px" : "120px", textOverflow: 'ellipsis'}} noWrap>
                              {careerDisplayName(career)}
                            </Typography>
                            {mdUp && (
                              <Divider orientation="vertical" flexItem />
                            )}
                            {mdUp && (
                              <Typography sx={{ minWidth: 200, color: career?.is_hidden_career_period ? "text.disabled" : "text.primary" }}>
                                {careerDisplayPeriod(career)}
                              </Typography>
                            )}
                            <Button
                              size="small"
                              startIcon={<Iconify icon="eva:eye-off-fill" />}
                              sx={{ color: "text.secondary", px: 1, minWidth: career?.is_hidden_company_name ? 100 : 75 }}
                              onClick={(e) => openHideCareerDialog(e, career, index)}
                              disableFocusRipple
                            >
                              {career?.is_hidden_company_name ? '가리기 수정' : '가리기'}
                            </Button>
                          </Stack>
                          {mdUp && (
                            <Button size="small"
                              startIcon={<SvgIconStyle src={"/icons/icon-pencil-mono.svg"} sx={{ width: 16, height: 16 }} />}
                              sx={{ color: "text.secondary", px: 1, minWidth: 88 }}
                              onClick={(e) => openModifyCareerDialog(career, index)}
                              disableFocusRipple
                            >
                              경력 수정
                            </Button>
                          )}
                          {!mdUp && (
                            <IconButton
                              edge="end"
                              size="small"
                              sx={{ color: "text.secondary" }}
                              onClick={(e) => openModifyCareerDialog(career, index)}
                              disableFocusRipple
                            >
                              <SvgIconStyle src={"/icons/icon-pencil-mono.svg"} sx={{ width: 16, height: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                        {!mdUp && (
                          <Typography variant="body2" sx={{ color: career?.is_hidden_career_period ? "text.disabled" : "text.primary" }}>
                            {careerDisplayPeriod(career)}
                          </Typography>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Typography sx={{ color: !!career?.description ? "text.secondary" : "text.disabled" }} noWrap>
                          {career?.description || '진행한 업무 상세 내용을 작성해주세요.'}
                        </Typography>
                      </Box>
                      ))}
                    </Stack>
                    {getValues().careers?.length === 0 && (
                      <Button
                        variant="outlined"
                        startIcon={<Iconify icon="ic:round-plus" />}
                        color={errors.careers ? 'error' : 'primary'}
                        sx={{ justifyContent: 'start', width: '165px', borderRadius: 1 }}
                        onClick={openCareerDialog}
                        disableFocusRipple
                      >
                        경력 사항 입력하기
                      </Button>
                    )}
                    {getValues().careers?.length > 0 && (
                    <Button
                      variant="text"
                      startIcon={<Iconify icon="ic:round-plus" />}
                      sx={{ mt: 1, justifyContent: 'start', width: '155px', borderRadius: 1, color: 'text.secondary' }}
                      onClick={openCareerDialog}
                      disableFocusRipple
                    >
                      경력 사항 추가하기
                    </Button>
                    )}
                    {errors.careers && getValues().careers?.length === 0 && (
                      <Typography variant="caption" sx={{ color: 'red', mt: 1 }}>
                        {errors.careers.message}
                      </Typography>
                    )}
                  </Stack>
                  {/* _career */}
                  {/* AI generated career */}
                  {application?.ai_careers?.length > 0 && (
                  <Box
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.chart['violet'][0]}`,
                      borderRadius: 2,
                      p: 3,
                    }}
                  >
                    <Stack spacing={1}>
                      <Box>
                        <Label sx={{ color: 'chart.violet.0', backgroundColor: (theme) => alpha(`${theme.palette.chart['violet'][2]}`, 0.3), }}>
                          <SvgIconStyle src={"/icons/ic_ai_generated.svg"} sx={{ width: 16, height: 16 }} /> AI 추천
                        </Label>
                      </Box>
                      <Typography variant="subtitle1">
                        AI가 작성한 경력 사항을 적용해보세요!
                      </Typography>
                      <Typography variant="body2">
                        크루님이 제출한 정보를 참고하여 AI가 경력 사항을 새로 작성했어요. 내용을 확인해보시고, 괜찮다면 프로필에 적용해보세요.
                      </Typography>
                    </Stack>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                      {application.ai_careers.map((aiCareer, index) => (
                        <Box
                          key={index}
                          sx={{ p: 2, borderRadius: 1, border: (theme) => `1px solid ${theme.palette.grey[500_32]}`, '&:hover': { cursor: 'pointer' } }}
                          onClick={() => openAICareerDialog(aiCareer, index)}
                        >
                          <Box sx={{ alignItems: 'center', justifyContent: 'space-between', display: 'flex', }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ width: 1 }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ overflow: "hidden" }}>
                                <SvgIconStyle src={"/icons/ic_ai_generated.svg"} sx={{ color: "chart.violet.0", width: 24, height: 24 }} />
                                <Typography variant="subtitle1" sx={{ width: '100%', maxWidth: mdUp ? "100%" : smUp ? "300px" : "120px", textOverflow: 'ellipsis'}} noWrap>
                                  {aiCareer.company_name}
                                </Typography>
                              </Stack>
                              {smUp && (
                              <Button
                                sx={{ color: "common.white", backgroundColor: "chart.violet.0", px: 2, minWidth: 110, ":hover": { backgroundColor: "chart.violet.1" } }}
                                disableFocusRipple
                                onClick={(e) => {e.stopPropagation(); e.preventDefault(); openAICareerDialog(aiCareer, index)}}
                              >
                                내용 확인하기
                              </Button>
                              )}
                            </Stack>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Typography sx={{ color: "text.secondary" }} noWrap>
                            {aiCareer.description}
                          </Typography>
                          {!smUp && (
                          <Stack alignItems="flex-end" sx={{ mt: 1 }}>
                            <Button
                              sx={{ color: "common.white", backgroundColor: "chart.violet.0", px: 2, minWidth: 110, ":hover": { backgroundColor: "chart.violet.1" } }}
                              disableFocusRipple
                              onClick={(e) => {e.stopPropagation(); e.preventDefault(); openAICareerDialog(aiCareer, index)}}
                            >
                              내용 확인하기
                            </Button>
                          </Stack>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                  )}
                  
                  <RHFTextField
                    name="profile"
                    label="크루 소개"
                    multiline
                    rows={6}
                    placeholder={profilePlaceholder}
                  />

                  {/* AI generated career */}
                  {application?.active_ai_profile && (
                  <Box
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.chart['violet'][0]}`,
                      borderRadius: 2,
                      p: 3,
                    }}
                  >
                    <Stack spacing={1}>
                      <Box>
                        <Label sx={{ color: 'chart.violet.0', backgroundColor: (theme) => alpha(`${theme.palette.chart['violet'][2]}`, 0.3), }}>
                          <SvgIconStyle src={"/icons/ic_ai_generated.svg"} sx={{ width: 16, height: 16 }} /> AI 추천
                        </Label>
                      </Box>
                      <Typography variant="subtitle1">
                        AI가 작성한 크루 소개로 바꿔보세요!
                      </Typography>
                      <Typography variant="body2">
                        크루님이 제출한 정보를 참고하여 AI가 크루 소개를 새로 작성했어요. 확인해보시고 내용이 괜찮다면, 바꿔보세요.
                      </Typography>
                      <Box sx={{ p: 2, borderRadius: 1, border: (theme) => `1px solid ${theme.palette.grey[500_32]}`, gap: 2, display: 'flex', flexDirection: 'column' }}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          <SvgIconStyle src={"/icons/ic_ai_generated.svg"} sx={{ color: "chart.violet.0", minWidth: 24, minHeight: 24 }} />
                          <Typography sx={{ whiteSpace: "pre-wrap" }}>
                            {application.active_ai_profile}
                          </Typography>
                        </Stack>
                        <Stack direction={{ xs: "column-reverse", sm: "row" }} justifyContent="flex-end" alignItems="center" spacing={1}>
                          <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => setIsOpenAIProfileDelete(true)}
                            disableFocusRipple
                            fullWidth={!smUp}
                          >
                            바꾸지 않을래요
                          </Button>
                          <Button
                            sx={{ 
                              color: "common.white", backgroundColor: "chart.violet.0", px: 2,
                              ":hover": { backgroundColor: "chart.violet.1" }
                             }}
                            onClick={() => setIsOpenAIProfileConfirm(true)}
                            disableFocusRipple
                            fullWidth={!smUp}
                          >
                            AI 크루 소개로 바꾸기
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                  )}

                  <RHFTextField
                    name="years"
                    label="경력 연차 정보"
                    placeholder="숫자만 입력해주세요"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">년</InputAdornment>
                    }}
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                      }
                    }}
                  />
                  
                  <RHFFileOrURL
                    fieldName="resume"
                    fieldLabel="이력서, 링크드인"
                    titleText="이력서, 링크드인 프로필"
                    defaultOption={resumeType}
                    openFile={openFile}
                  />
                  
                  {needPortfolio && (
                    <RHFFileOrURL
                      fieldName="portfolio"
                      fieldLabel="포트폴리오"
                      titleText="포트폴리오 (선택)"
                      defaultOption={portfolioType}
                      openFile={openFile}
                    />
                  )}
    
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3, justifyContent: "flex-end" }}>
                <LoadingButton
                  size="large"
                  variant="contained"
                  fullWidth={!smUp}
                  onClick={handleSubmit(onSubmit, submitError)}
                  loading={isSubmitting}
                >
                  프로필 저장하기
                </LoadingButton>
              </CardActions>
            </Card>

            {/* 커리어 상태 카드 */}
            {isProfileEmploymentLoading && (
              <Stack spacing={3}>
                <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
                  <CardContent>
                    <Stack spacing={3}>
                      <Skeleton variant="text" height={56} />
                      <Skeleton variant="text" height={56} />
                      <Skeleton variant="text" height={56} />
                      <Skeleton variant="text" height={56} />
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            )}
            {!isProfileEmploymentLoading && (
              <CareerStatusCard
                profile={profileEmployment}
              />
            )}
            {/* 추가 스킬 카드 */}
            {isProfileSkillLoading && (
              <Stack spacing={3}>
                <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
                  <CardContent>
                    <Stack spacing={3}>
                      <Skeleton variant="text" height={112} />
                      <Skeleton variant="text" height={56} />
                      <Skeleton variant="text" height={56} />
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            )}
            {!isProfileSkillLoading && (
              <AdditionalSkillCard
                profile={profileSkill}
                skillOption={skillOption}
              />
            )}
          </Stack>
        </FormProvider>
      )}
      <AddCareerDialog
        isOpen={isOpenAddCareerDialog}
        closeDialog={closeAddCareerDialog}
        addCareer={addCareer}
        hasWorkingCareer={hasWorkingCareer}
      />
      <ModifyCareerDialog
        isOpen={isOpenModifyCareerDialog}
        closeDialog={closeModifyCareerDialog}
        modifyCareer={modifyCareer}
        removeCareer={removeCareer}
        career={selectedCareer}
        careerIndex={selectedCareerIndex}
        hasWorkingCareer={hasWorkingCareer}
      />
      <HideCareerDialog
        isOpen={isOpenHideCareerDialog}
        closeDialog={closeHideCareerDialog}
        career={selectedCareer}
        hideCareer={hideCareer}
        unHideCareer={unHideCareer}
        careerIndex={selectedCareerIndex}
      />
      <ProfilePreviewDialog
        isOpen={isOpenProfilePreview}
        closeDialog={() => setIsOpenProfilePreview(false)}
        application={application}
      />
      <MatchingOnDialog
        isOpen={isOpenTurnOnMatching}
        closeDialog={() => setIsOpenTurnOnMatching(false)}
      />
      <AIProfileConfirmDialog
        isOpen={isOpenAIProfileConfirm}
        closeDialog={() => setIsOpenAIProfileConfirm(false)}
        reload={getApplication}
      />
      <AIProfileDeleteDialog
        isOpen={isOpenAIProfileDelete}
        closeDialog={() => setIsOpenAIProfileDelete(false)}
        reload={getApplication}
      />
      <AICareerDialog
        isOpen={isOpenAICareer}
        closeDialog={closeAICareerDialog}
        career={selectedAICareer}
        careerIndex={selectedAICareerIndex}
        addCareer={addCareer}
        reload={getApplication}
      />
    </Grid>
  );
}
